import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Calendar, Trash2, Edit, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const Events = () => {
  const { hasPermission, isAdmin } = useAuth();
  const canView = hasPermission('events_view') || isAdmin();
  const canEdit = hasPermission('events_edit') || isAdmin();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    event_type: 'evento',
    event_category: 'com_atendimento',
    municipality: 'todos'
  });

  useEffect(() => {
    if (canView) {
      fetchEvents();
    } else {
      setLoading(false);
    }
  }, [selectedMonth, canView]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`, {
        params: { month: selectedMonth }
      });
      setEvents(response.data);
    } catch (error) {
      toast.error('Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, formData);
        toast.success('Evento atualizado com sucesso!');
      } else {
        await axios.post(`${API}/events`, formData);
        toast.success('Evento cadastrado com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description || '',
      date: event.date,
      event_type: event.event_type,
      event_category: event.event_category || 'com_atendimento',
      municipality: event.municipality || 'todos'
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Deseja realmente excluir este evento?')) {
      try {
        await axios.delete(`${API}/events/${id}`);
        toast.success('Evento removido com sucesso!');
        fetchEvents();
      } catch (error) {
        toast.error('Erro ao remover evento');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      event_type: 'evento',
      event_category: 'com_atendimento',
      municipality: 'todos'
    });
    setEditingEvent(null);
  };

  const groupEventsByDate = () => {
    const grouped = {};
    events.forEach(event => {
      if (!grouped[event.date]) {
        grouped[event.date] = [];
      }
      grouped[event.date].push(event);
    });
    return grouped;
  };

  const groupedEvents = groupEventsByDate();
  const sortedDates = Object.keys(groupedEvents).sort();

  // Se não tem permissão de visualização, mostrar mensagem
  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <Lock className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700 mb-2">Acesso Restrito</h2>
        <p className="text-slate-500">Você não tem permissão para visualizar a agenda.</p>
        <p className="text-slate-400 text-sm mt-1">Solicite acesso ao administrador.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Agenda e Feriados</h1>
          <p className="text-slate-600 mt-1">Gerencie eventos e feriados</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-event-button">
                <Plus className="h-4 w-4 mr-2" />
                Novo Evento
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingEvent ? 'Editar Evento' : 'Novo Evento'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  data-testid="event-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Ex: Dia das Crianças"
                />
              </div>
              <div>
                <Label htmlFor="date">Data *</Label>
                <Input
                  id="date"
                  type="date"
                  data-testid="event-date-input"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="event_type">Tipo *</Label>
                <select
                  id="event_type"
                  data-testid="event-type-select"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.event_type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setFormData({ 
                      ...formData, 
                      event_type: newType,
                      event_category: newType === 'feriado' ? 'sem_atendimento' : formData.event_category
                    });
                  }}
                >
                  <option value="evento">Evento</option>
                  <option value="feriado">Feriado</option>
                </select>
              </div>
              <div>
                <Label htmlFor="event_category">Atendimento *</Label>
                <select
                  id="event_category"
                  data-testid="event-category-select"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.event_category}
                  onChange={(e) => setFormData({ ...formData, event_category: e.target.value })}
                >
                  <option value="com_atendimento">Com atendimento</option>
                  <option value="sem_atendimento">Sem atendimento</option>
                </select>
                {formData.event_category === 'sem_atendimento' && (
                  <p className="text-xs text-amber-600 mt-1 font-medium">
                    A chamada deste dia será automaticamente justificada para todas as crianças
                  </p>
                )}
              </div>
              {(formData.event_type === 'feriado' || formData.event_category === 'sem_atendimento') && (
                <div>
                  <Label htmlFor="municipality">Município *</Label>
                  <select
                    id="municipality"
                    data-testid="event-municipality-select"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.municipality}
                    onChange={(e) => setFormData({ ...formData, municipality: e.target.value })}
                  >
                    <option value="todos">Todos</option>
                    <option value="biguacu">Biguaçu</option>
                    <option value="gov_celso_ramos">Governador Celso Ramos</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Eventos sem atendimento e feriados justificam automaticamente as faltas
                  </p>
                </div>
              )}
              <div>
                <Label htmlFor="description">Descrição</Label>
                <textarea
                  id="description"
                  data-testid="event-description-textarea"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detalhes do evento..."
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-event-button">
                  {editingEvent ? 'Atualizar' : 'Cadastrar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Month Selector */}
      <div className="bg-white rounded-xl shadow-md p-4 border border-slate-100">
        <div className="flex items-center gap-3">
          <Calendar className="h-5 w-5 text-slate-600" />
          <Label htmlFor="month-select">Selecione o Mês</Label>
          <Input
            id="month-select"
            type="month"
            data-testid="month-select-input"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-auto"
          />
        </div>
      </div>

      {/* Events List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="events-list">
          {sortedDates.map(date => (
            <div key={date} className="bg-white rounded-xl shadow-md p-6 border border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">
                {new Date(date + 'T00:00:00').toLocaleDateString('pt-BR', { 
                  weekday: 'long', 
                  day: '2-digit', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </h3>
              <div className="space-y-3">
                {groupedEvents[date].map(event => (
                  <div
                    key={event.id}
                    className={`p-4 rounded-lg border transition-all ${
                      event.event_type === 'feriado'
                        ? 'bg-red-50 border-red-200'
                        : event.event_category === 'sem_atendimento'
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-blue-50 border-blue-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="font-semibold text-slate-800">{event.title}</h4>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            event.event_type === 'feriado'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {event.event_type === 'feriado' ? 'Feriado' : 'Evento'}
                          </span>
                          {event.event_category === 'sem_atendimento' && (
                            <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full font-medium">
                              Sem atendimento
                            </span>
                          )}
                          {event.municipality && event.municipality !== 'todos' && (
                            <span className="text-xs px-2 py-1 bg-slate-100 rounded-full text-slate-600">
                              {event.municipality === 'biguacu' ? 'Biguaçu' : 'Governador Celso Ramos'}
                            </span>
                          )}
                        </div>
                        {event.description && (
                          <p className="text-sm text-slate-600">{event.description}</p>
                        )}
                      </div>
                      {canEdit && (
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(event)}
                            data-testid={`edit-event-${event.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(event.id)}
                            data-testid={`delete-event-${event.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && events.length === 0 && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center border border-slate-100">
          <Calendar className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Nenhum evento cadastrado para este mês</p>
        </div>
      )}
    </div>
  );
};

export default Events;
